<template>
  <div :data-id="panelId" class="flex flex-col gap-5 bg-slate-200">
    <div :data-id="panelId" class="px-6 py-5 bg-pure-white">
      <div
        :data-id="panelId"
        class="flex items-center justify-between gap-6 mb-1"
      >
        <BlTextBody2
          :data-id="panelId"
          class="truncate"
          :text="target.curriculum_name"
        />
        <BlIcon icon="close" :clickable="true" />
      </div>
      <div :data-id="panelId" class="mb-4">
        <BlTextSubtitle1 :data-id="panelId" :text="target.name" />
      </div>
      <div :data-id="panelId" class="mb-4">
        <BlTextBody2 :data-id="panelId" :text="target.description" class="whitespace-pre-line" />
      </div>
      <div :data-id="panelId" class="flex gap-2 mb-2">
        <BlIcon
          :data-id="panelId"
          icon="document-normal"
          color="slate-6"
          class="w-4 h-4"
        />
        <BlTextBody2 :data-id="panelId" :text="targetTypes[target.type]" />
      </div>
      <div :data-id="panelId" class="flex items-start gap-2 mb-2">
        <BlIcon
          :data-id="panelId"
          icon="goal"
          color="slate-6"
          class="w-4 mt-0.5"
        />
        <BlTextBody2 :data-id="panelId" :text="generateSuccessMetric(target)" />
      </div>
      <div
        :data-id="panelId"
        v-if="target.type.includes('Percentage')"
        class="flex gap-2"
      >
        <BlIcon
          :data-id="panelId"
          icon="tick-square"
          color="slate-6"
          class="w-4 h-4"
        />
        <BlTextBody2
          :data-id="panelId"
          :text="`${target.number_of_trial} trial(s)`"
        />
      </div>
      <div
        :data-id="panelId"
        v-if="target.type.includes('Pir')"
        class="flex gap-2"
      >
        <BlIcon :data-id="panelId" icon="timer" color="slate-6" class="w-5" />
        <BlTextBody2
          :data-id="panelId"
          :text="`${target.duration} minute(s)`"
        />
      </div>
    </div>

    <div
      :data-id="panelId"
      v-if="target.probing_enable"
      class="px-6 py-5 bg-pure-white"
    >
      <div :data-id="panelId" class="mb-1">
        <BlTextSubtitle2 :data-id="panelId" text="Probing" />
      </div>
      <div :data-id="panelId" class="mb-4">
        <BlTextBody2
          :data-id="panelId"
          text="Probing can be employed to assess a skill at various stages of your therapy."
        />
      </div>
      <div :data-id="panelId" class="flex items-start gap-2 mb-2">
        <BlIcon
          :data-id="panelId"
          icon="goal"
          color="slate-6"
          class="w-4 mt-0.5"
        />
        <BlTextBody2 :data-id="panelId" :text="`≥ ${target.probing_goal}%`" />
      </div>
      <div :data-id="panelId" class="flex gap-2">
        <BlIcon
          :data-id="panelId"
          icon="tick-square"
          color="slate-6"
          class="w-4 h-4"
        />
        <BlTextBody2
          :data-id="panelId"
          :text="`${target.probing_number_of_trial} trial(s)`"
        />
      </div>
    </div>

    <div
      :data-id="panelId"
      v-if="target.type.includes('Prompting')"
      class="px-6 py-5 bg-pure-white"
    >
      <div :data-id="panelId" class="mb-1">
        <BlTextSubtitle2 :data-id="panelId" text="Prompting" />
      </div>
      <div :data-id="panelId" class="mb-4">
        <BlTextBody2
          :data-id="panelId"
          text="Here are the prompts used in this target:"
        />
      </div>
      <div :data-id="panelId" class="flex flex-col gap-4">
        <div
          v-for="prompt in target.prompts"
          :key="prompt.id"
          :data-id="panelId"
          class="flex items-center h-6 gap-2"
        >
          <BlIcon
            :data-id="panelId"
            v-if="prompt.shape && prompt.color"
            class="w-5"
            :icon="'prompt-' + prompt.shape"
            :color="prompt.color"
          />
          <BlTextBody2 :data-id="panelId" :text="prompt.name" />
        </div>
      </div>
    </div>

    <div
      :data-id="panelId"
      v-if="actionRecommendation"
      class="px-6 py-5 bg-pure-white"
    >
      <div :data-id="panelId" class="flex items-center gap-2 mb-4">
        <div
          :data-id="panelId"
          class="p-1 bg-orange-300"
          :style="{ borderRadius: '4px' }"
        >
          <BlIcon :data-id="panelId" icon="tooltip" class="w-5 h-5" />
        </div>
        <BlTextSubtitle2 :data-id="panelId" text="Action recommendations" />
      </div>
      <div
        :data-id="panelId"
        class="p-4 border rounded bg-slate-100 border-slate-400"
      >
        <div :data-id="panelId" class="mb-4 space-y-1">
          <BlTextSubtitle3 :data-id="panelId" text="Passing success metric" />
          <div :data-id="panelId" class="flex flex-wrap items-center gap-2">
            <BlTextBody2
              :data-id="panelId"
              text="Updating the current target's status from"
            />
            <StatusChip
              :data-id="panelId"
              status="in_progress"
              class="text-xs"
            />
            <BlTextBody2 :data-id="panelId" text="to" />
            <StatusChip :data-id="panelId" status="mastered" class="text-xs" />
          </div>
        </div>
        <div
          :data-id="panelId"
          v-if="
            actionRecommendation.total_success === null &&
              actionRecommendation.consecutive_success === null
          "
        >
          <BlTextBody2
            :data-id="panelId"
            class="italic"
            text="Currently, no action has been set for this recommendation."
          />
        </div>
        <div :data-id="panelId" v-else class="pl-6 space-y-4">
          <div
            :data-id="panelId"
            v-if="actionRecommendation.total_success !== null"
            class="space-y-1"
          >
            <div :data-id="panelId" class="flex items-center gap-2">
              <BlTextSubtitle3 :data-id="panelId" text="Total success" />
            </div>
            <div :data-id="panelId" class="flex flex-wrap items-center gap-1">
              <BlTextBody2
                :data-id="panelId"
                text="Receive a recommendation after"
              />
              <BlTextBody2 :data-id="panelId" text="completing" />
              <BlTextBody2
                :data-id="panelId"
                class="font-bold"
                :text="`at least ${actionRecommendation.total_success}`"
              />
              <BlTextBody2 :data-id="panelId" text="successful" />
              <BlTextBody2 :data-id="panelId" text="session(s)." />
            </div>
          </div>
          <div
            :data-id="panelId"
            v-if="actionRecommendation.consecutive_success !== null"
            class="space-y-1"
          >
            <div :data-id="panelId" class="flex items-center gap-2">
              <BlTextSubtitle3 :data-id="panelId" text="Consecutive success" />
            </div>
            <div :data-id="panelId" class="flex flex-wrap items-center gap-1">
              <BlTextBody2
                :data-id="panelId"
                text="Receive a recommendation only if the"
              />
              <BlTextBody2
                :data-id="panelId"
                text="session is successful for"
              />
              <BlTextBody2
                :data-id="panelId"
                class="font-bold"
                :text="
                  `${actionRecommendation.consecutive_success} consecutive`
                "
              />
              <BlTextBody2
                :data-id="panelId"
                class="font-bold"
                :text="`session(s).`"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import BlIcon from "../../BlIcon.vue";
import BlTextBody2 from "../../BlTextBody2.vue";
import BlTextSubtitle1 from "../../BlTextSubtitle1.vue";
import BlTextSubtitle2 from "../../BlTextSubtitle2.vue";
import BlTextSubtitle3 from "../../BlTextSubtitle3.vue";
import StatusChip from "../../clients/target/StatusChip.vue";

export default {
  name: "SuperAdminDatabankDetails",
  props: ["target", "panelId"],
  components: {
    BlIcon,
    BlTextBody2,
    BlTextSubtitle1,
    BlTextSubtitle2,
    BlTextSubtitle3,
    StatusChip
  },
  data() {
    return {
      targetTypes: {
        "Target::Percentage": "Percentage",
        "Target::Pir": "Partial interval recording",
        "Target::Duration": "Duration",
        "Target::Frequency": "Frequency",
        "Target::Prompting": "Prompting"
      }
    };
  },
  computed: {
    actionRecommendation() {
      if (!this.target) return null;
      return this.target.action_recommendations &&
        this.target.action_recommendations.at(0)
        ? this.target.action_recommendations.at(0)
        : {
          id: null,
          target_id: this.target.id,
          total_success: null,
          consecutive_success: null
        };
    }
  },
  methods: {
    generateSuccessMetric(target) {
      let prefix = "";
      let goalText = "";
      let suffix = "";
      // prefix
      if (target.success_metric === "equal to or greater than goal") {
        prefix = "≥ ";
      }
      if (target.success_metric === "less than goal") {
        prefix = "< ";
      }
      // goal text
      if (target.type.includes("Duration")) {
        goalText = target.goal_time;
      } else if (target.type.includes("Prompting")) {
        goalText = `${target.success_metric} prompt min. ${target.goal}`;
      } else {
        goalText = target.goal;
      }
      // suffix
      if (target.type.includes("Percentage") || target.type.includes("Pir")) {
        suffix = "%";
      }
      if (
        target.type.includes("Frequency") ||
        target.type.includes("Prompting")
      ) {
        suffix = " attempt(s) per session";
      }
      return prefix + goalText + suffix;
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/superAdmin/databank/PanelDetails.vue